import type { SceneStatus } from './labSceneRuntime';

type Schedule = (callback: () => void, delay: number) => () => void;
const retryDelays = [750, 2500, 8000];
const scheduleTimeout: Schedule = (callback, delay) => {
  const timer = setTimeout(callback, delay);
  return () => clearTimeout(timer);
};

// После трёх сбоев ждём нового online/focus/visible, а не перегружаем недоступный GPU.
export function createSceneRecovery(restart: () => void, schedule: Schedule = scheduleTimeout) {
  let status: SceneStatus = 'loading';
  let available = false;
  let disposed = false;
  let attempts = 0;
  let cancelRetry: (() => void) | undefined;
  let cancelStable: (() => void) | undefined;
  const healthy = () => status === 'ready' || status === 'transitioning';
  const clear = () => { cancelRetry?.(); cancelStable?.(); cancelRetry = cancelStable = undefined; };
  const reconcile = () => {
    if (disposed || !available) return;
    if (status === 'error' && !cancelRetry && attempts < retryDelays.length) {
      cancelRetry = schedule(() => {
        cancelRetry = undefined;
        if (disposed || !available || status !== 'error') return;
        attempts++;
        status = 'loading';
        restart();
      }, retryDelays[attempts]);
    } else if (healthy() && attempts && !cancelStable) {
      // Один удачный кадр не обнуляет бюджет при повторяющемся падении контекста.
      cancelStable = schedule(() => { cancelStable = undefined; attempts = 0; }, 30_000);
    }
  };
  return {
    setStatus(next: SceneStatus) {
      if (disposed || status === next) return;
      const wasHealthy = healthy();
      status = next;
      if (!(wasHealthy && healthy())) clear();
      reconcile();
    },
    setAvailable(next: boolean) {
      if (disposed || available === next) return;
      available = next;
      if (!available) clear();
      else {
        if (status === 'error') attempts = 0;
        reconcile();
      }
    },
    wake() {
      if (disposed || !available || status !== 'error') return;
      attempts = 0;
      // Событие восстановления не создаёт второй параллельный перезапуск.
      reconcile();
    },
    dispose() { if (!disposed) { disposed = true; clear(); } },
  };
}
